import { useEffect, useState } from 'react';
import { useDragDropManager } from 'react-dnd';

import { FAVORITE_DRAG_TYPE } from './constants';

import type { RefObject } from 'react';
import type { FavoriteDragItem } from './constants';

/**
 * 지수 평활 시간 상수(ms) — 프리뷰가 커서를 따라가는 속도의 유일한 튜닝 노브.
 * @description
 * - 커서와의 남은 거리가 t ms 후 exp(-t/τ) 비율로 줄어든다: τ ms에 63%, 약 3τ ms에 95% 해소(체감상 "도착").
 * - 값을 줄이면 커서에 빨리 붙는다(빠릿함). 단 너무 작으면 다음 좌표가 오기 전에 도착해버려,
 *   좌표가 띄엄띄엄 들어오는 Safari에서 계단식 끊김이 다시 보인다(τ는 입력 간격을 메울 정도여야 함).
 * - 값을 키우면 더 부드럽지만, 빠른 드래그 시 프리뷰가 커서를 뒤따라오는 거리(트레일링)가 길어진다.
 * - dt 기반(α = 1 - exp(-dt/τ))이라 60/120Hz 디스플레이에서 체감이 동일하다. 권장 튜닝 범위 30~100.
 */
const LERP_TAU_MS = 30;
/**
 * 목표와의 거리가 이 값(px) 미만이면 목표에 스냅하고 rAF 루프를 정지한다.
 * @description
 * - 지수 평활은 목표에 수학적으로 도달하지 않으므로 루프 종료 조건이 필요하다.
 * - 커서 이동 중에는 영향이 없고 커서가 멈춘 뒤 정착 순간에만 관여한다(따라가는 속도와 무관).
 *   키우면 막판 미세 점프가 보일 수 있고, 0.5px는 디바이스 픽셀 이하라 조정할 이유가 없는 값.
 */
const SNAP_EPSILON_PX = 0.5;

/**
 * 드래그 프리뷰가 커서를 따라가도록 previewRef의 transform을 직접 write하는 훅.
 * @description
 * - monitor를 직접 구독해 ref의 transform에 직접 써서 React 리렌더 없이 위치를 갱신한다.
 *   아이템 콘텐츠만 드래그 시작/종료 시 1회 state로 갱신한다(매 이동 리렌더 회피).
 * - 좌표 소스(HTML5 dragover)를 WebKit이 throttle해 Safari에서 갱신이 띄엄띄엄 들어오므로,
 *   offset은 목표 좌표로만 저장하고 rAF 루프가 지수 평활(lerp)로 따라가며 write해 끊김을 완화한다.
 *   시각 전용 보간이다 — 드롭 커밋 위치는 dragState(hover)가 결정하므로 프리뷰 지연은 동작에 영향 없음.
 * @returns 드래그 중인 아이템. 드래그 중이 아니면 null
 */
export const useDragPreviewFollower = (
  previewRef: RefObject<HTMLDivElement | null>,
): FavoriteDragItem | null => {
  const manager = useDragDropManager();
  const [item, setItem] = useState<FavoriteDragItem | null>(null);

  useEffect(() => {
    const monitor = manager.getMonitor();

    const target = { x: 0, y: 0 };
    const current = { x: 0, y: 0 };
    // 이번 드래그에서 유효 offset을 받은 적 있는지. false→true 전환 시 lerp 없이 스냅(fly-in 방지)
    let hasTarget = false;
    let rafId: number | null = null;
    let lastTs: number | null = null;

    // 위치 write: ref에 직접 write → 리렌더 없음
    const write = () => {
      const el = previewRef.current;
      if (!el) return;
      el.style.transform = `translate3d(${current.x}px, ${current.y}px, 0)`;
      el.style.visibility = 'visible';
    };

    const stopLoop = () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = null;
      lastTs = null;
    };

    // rAF 보간 루프: 매 프레임 current를 target으로 지수 평활 이동. 수렴하면 스냅 후 정지
    const step = (ts: number) => {
      rafId = null;
      const dt = lastTs === null ? 16.7 : ts - lastTs;
      lastTs = ts;
      const alpha = 1 - Math.exp(-dt / LERP_TAU_MS);
      current.x += (target.x - current.x) * alpha;
      current.y += (target.y - current.y) * alpha;
      const converged =
        Math.abs(target.x - current.x) < SNAP_EPSILON_PX &&
        Math.abs(target.y - current.y) < SNAP_EPSILON_PX;
      if (converged) {
        current.x = target.x;
        current.y = target.y;
      }
      write();
      if (!converged) {
        rafId = requestAnimationFrame(step);
      } else {
        lastTs = null;
      }
    };

    // 목표 좌표 갱신: 첫 offset(드래그 시작·null 후 재개)은 스냅, 이후에는 rAF 루프가 따라간다
    const positionPreview = () => {
      const offset = monitor.getSourceClientOffset();
      if (!offset) {
        // Safari dragover 중 일시 null 가능 — 숨기고, 재개 시 화면을 가로지르는 lerp 대신 스냅하도록 리셋
        hasTarget = false;
        stopLoop();
        const el = previewRef.current;
        if (el) el.style.visibility = 'hidden';
        return;
      }
      target.x = offset.x;
      target.y = offset.y;
      if (!hasTarget) {
        hasTarget = true;
        current.x = target.x;
        current.y = target.y;
        write();
        return;
      }
      if (rafId === null) {
        lastTs = null;
        rafId = requestAnimationFrame(step);
      }
    };

    // 콘텐츠 갱신: 드래그 시작/종료 시에만 state 갱신
    const syncItem = () => {
      const isDragging = monitor.isDragging() && monitor.getItemType() === FAVORITE_DRAG_TYPE;
      setItem(isDragging ? (monitor.getItem() as FavoriteDragItem) : null);
      if (!isDragging) {
        stopLoop();
        hasTarget = false;
      }
    };

    const unsubscribeOffset = monitor.subscribeToOffsetChange(positionPreview);
    const unsubscribeState = monitor.subscribeToStateChange(syncItem);
    return () => {
      unsubscribeOffset();
      unsubscribeState();
      stopLoop();
    };
  }, [manager, previewRef]);

  return item;
};
